<template>
  <div class="PrivacyPolicy">
    <h1 class="PrivacyPolicy__title">Privacy Policy</h1>
    <h3 class="PrivacyPolicy__subtitle">Last Updated: February 21, 2022</h3>
    <div class="PrivacyPolicy__text">
      <p>
        This privacy policy <span class="bold">(“Privacy Policy”)</span> governs how we, <span class="bold">Capsulab Ltd.</span> an Israeli company located at "Aba-Hilel 16, Ramat Gan" (<span class="bold">“Capsulab”</span>, <span class="bold">“we”</span>, <span class="bold">“our”</span> or <span class="bold">“us”</span>) use,
        collect and store personal information that we collect or receive from or about you (<span class="bold">“you”</span>) in connection with your use of our website found at
        <a href="https://capsulab.web.app/">https://capsulab.web.app/</a>.
      </p>
      <p>
        Please read this Privacy Policy carefully, so you can fully understand our practices in relation to personal data.
      </p>
      <p>
        <span class="PrivacyPolicy__text-underline">Important note:</span> Nothing in this Privacy Policy is intended to limit in any way your statutory rights, including your rights to a remedy or other means of enforcement.
      </p>
    </div>
    <div class="PrivacyPolicy__text">
      <ol class="PrivacyPolicy__list">
        <span class="underline bold">Table of contents:</span>
        <li>What information we collect, why we collect it, and how it is used</li>
        <li>How we protect and retain your Personal Information</li>
        <li>How we share your Personal Information</li>
        <li>Your privacy rights.</li>
        <li>Use by children</li>
        <li>Interaction with third party products</li>
        <li>Analytic tools</li>
        <li>Specific provisions applicable under California privacy law</li>
        <li>Contact us</li>
      </ol>
    </div>
    <div class="PrivacyPolicy__text">
      This Privacy Policy can be updated from time to time and, therefore, we ask you to check back periodically for the latest version of this Privacy Policy. If we implement
      material changes in the way we use your personal information, in a manner that is different from that stated at the time of collection, we will notify you by posting a
      notice on our Website or App (or by other means) and take any additional steps as required by applicable law.
    </div>
    <div class="PrivacyPolicy__text">
      <ol class="PrivacyPolicy__list">
        <li class="list-item">
          <span class="list-item-title">WHAT INFORMATION WE COLLECT, WHY WE COLLECT IT, AND HOW IT IS USED</span>
          <ol class="PrivacyPolicy__letter-list">
            <span class="underline"><pre>(i)        We Process the Following Personal Information:</pre></span>
            <li>
              <span class="bold">Information You Provide Directly to Us.</span> We collect the following information you provide directly to us, including, without
              limitation, information when you browse the Website and complete surveys or webforms (for example, making an enquiry or
              subscribing to receive updates): full name, address, email address, phone number, as well as any other information that you
              decide to provide/supply us with when you communicate with us (via email, Website or telephone). We may also contact you directly,
              and such calls may be monitored or recorded.
            </li>
            <li>
              <span class="bold">Information from Other Sources.</span> We may also obtain information about you from other sources, including publicly- or
              commercially- available information, and through third-party data platforms, partners and service providers.
            </li>
            <li>
              <span class="bold">Automatic Data Collection.</span> We may automatically collect certain information through your use of the Website, such as your
              Internet protocol (IP) address, cookie identifiers and other device identifiers that are automatically assigned to your device,
              browser type and language, geo-location information, hardware type, operating system, internet service provider and other
              information about actions taken through the use of the Website
            </li>
          </ol>
          <ol class="PrivacyPolicy__letter-list" type="a">
            <span class="underline"><pre>(ii)        We process information for the following purposes</pre></span>
            <li>
              <span class="bold">To provide you with the Website.</span> Capsulab will use your personal information to interact with you in
              connection with your use of the Website. For example: (i) if you make purchases via the Website or request assistance in
              connection with your use of the Website; (ii) to track transactions conducted by you through our Website; (iii) to send you
              more information about products, services and offerings; and (iv) to personalize your experience with the Website. We may also
              use your information to contact you about incomplete applications or other forms and submissions available through the Website.
            </li>
            <li>
              <span class="bold">For Administrative Purposes.</span> We may use your personal information (i) to respond to your questions,
              comments, and other requests for customer support, or information, including information about potential or future services;
              (ii) for internal quality control purposes; (iii) to establish a business relationship; and (i) to generally administer the Website.
            </li>
            <li>
              <span class="bold">To improve the Services.</span> We may use your personal information (in addition to de-identified and
              aggregated information, as described below) to analyze and improve the Services. For example, we may utilize customer
              information to develop new product features and improve existing offerings.
            </li>
            <li>
              <span class="bold">To Market the Services.</span> We may use your personal information to market Capsulab products and services.
              Such use includes (i) notifying you about offers relating to our products and services, that may be of interest to you;
              (ii) tailoring content, advertisements, and offers for you, including, targeting and re-targeting practices; (iii) conducting
              market research; (iv) developing and marketing new products and services, and to measure interest in our products and services;
              (v) other purposes disclosed at the time you provide information; and (vi) as you otherwise consent.
            </li>
            <li>
              <span class="bold">Security purposes.</span> Some of the abovementioned information will be used for detecting, taking steps to
              prevent and prosecuting fraud or other illegal activity; to identify and repair errors; to conduct audits; and for security
              purposes. Information may also be used to comply with applicable laws, with investigations performed by the relevant authorities,
              law enforcement purposes, and/or to exercise or defend legal claims.
            </li>
            <li>
              <span class="bold">De-identified and Aggregated Information Use.</span> In certain cases, we may or will anonymize or
              de-identify your personal information and further use it for internal and external purposes, including, without limitation,
              to improve the Website, our products and services, and for research purposes. We may use this anonymous or de-identified
              information and/or disclose it to third parties without restrictions (for example, in order to improve our services and enhance
              your experience with them).
            </li>
            <li>
              <span class="bold">Cookies and Similar Technologies.</span> We, as well as third parties that provide content, advertising, or
              other functionality on the Website, may use cookies, pixel tags, local storage, and other technologies (“Technologies”) to
              automatically collect information through the Website. We use Technologies that are essentially small data files placed on
              your device that allow us to record certain pieces of information whenever you visit or interact with the Website. If you would
              like to opt out of the Technologies we employ on the Website, you may do so by blocking, deleting, or disabling them as your
              browser or device permits
            </li>
          </ol>
        </li>
        <li class="list-item">
          <span class="list-item-title">HOW WE PROTECT AND RETAIN YOUR INFORMATION</span>
          <ol class="PrivacyPolicy__sublist">
            <li> <span class="number"></span>
              <span class="text">
                <span class="underline">Security.</span> We have implemented and maintain reasonable technical, organizational and security
                measures designed to protect your personal information. However, please note that we cannot guarantee that the personal
                information will not be compromised as a result of unauthorized penetration to our servers. As the security of information
                depends in part on the security of the computer, device or network you use to communicate with us and the security you use
                to protect your user IDs and passwords, please make sure to take appropriate measures to protect this information.
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                <span class="underline">Retention of your information.</span> Your personal information will be stored until we delete our
                records, and we proactively delete it, or if you send a valid deletion request. Please note that in some circumstances we
                may store your personal information for longer periods of time, for example (i) where we are required to do so in
                accordance with legal, regulatory, tax or accounting requirements, (ii) for us to have an accurate record of your dealings
                with us in the event of any complaints or challenges, and/or (iii) if we reasonably believe there is a prospect of
                litigation relating to your information or dealings
              </span>
            </li>
          </ol>
        </li>
        <li class="list-item">
          <span class="list-item-title">HOW WE SHARE YOUR INFORMATION</span>
          In addition to the recipients described above, we may share your personal information as follows:
          <ol class="PrivacyPolicy__sublist">
            <li>
              <span class="number"></span>
              <span class="text">
                The information we gather is shared with our partners and other third parties. We disclose your personal information as is
                necessary for us to provide you with the Website, as well as any other services you expect us to provide. Please Note: Third
                party vendors have their own privacy policies and we are not responsible for these policies nor the products and/or services
                offered by such third party vendors, so please make sure to review the privacy policy of any third party vendor from whom
                you may purchase and/or order products or services.
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                We may also share your personal information with our affiliated companies.
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                We may use third party service providers to process your personal information for the purposes outlined above, including,
                without limitation
                <ol class="PrivacyPolicy__sublist-sub">
                  <li>
                    <span class="number"></span>
                    <span class="text">
                      With cloud service providers for hosting purposes;
                    </span>
                  </li>
                  <li>
                    <span class="number"></span>
                    <span class="text">
                      With websites and web content creation platforms in order to help us manage our Website;
                    </span>
                  </li>
                  <li>
                    <span class="number"></span>
                    <span class="text">
                      With email providers, marketing, CRM, other similar tool providers and/or sales dialer software;
                    </span>
                  </li>
                  <li>
                    <span class="number"></span>
                    <span class="text">
                      With analytic companies, in order to help us understand and analyze information we collect in accordance with this policy;
                    </span>
                  </li>
                  <li>
                    <span class="number"></span>
                    <span class="text">
                      With billing companies; and/or
                    </span>
                  </li>
                  <li>
                    <span class="number"></span>
                    <span class="text">
                      With data validation and enrichment companies.
                    </span>
                  </li>
                </ol>
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                To the extent necessary, with regulators, courts, banks or competent authorities, to comply with applicable laws, regulations
                and rules (including, without limitation, federal, state or local laws), and requests of law enforcement, regulatory and other
                governmental agencies or if required to do so by court order, as well as for internal compliance procedures and to protect
                the safety, security, and integrity of Capsulab, the Website, customers, employees, property, and/or the public.
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                If, in the future, we sell or transfer, or we consider selling or transferring, some or all of our business, shares or assets
                to a third party, we will disclose your personal information to such third party (whether actual or potential) in connection
                with the foregoing events (including, without limitation, our current or potential investors). In the event that we are acquired
                by, or merged with, a third party entity, or in the event of bankruptcy or a comparable event, we reserve the right to sell and
                transfer your personal information in connection with the foregoing events.
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                Certain social networking services ("SNS") may link-to the Website (e.g., Instagram, Facebook and/or LinkedIn). These SNS
                maintain their own privacy policies and policies relating to data collection, retention and use, and to the extent that you
                elect to utilize the SNS linking within the Website, you acknowledge and agree to comply with any of these third party terms,
                in addition to acknowledging, that except where prohibited by law, Capsulab is not liable or responsible for any use of the
                SNS that you elect to engage in. These SNS may receive your data when you visit or use their services or through third parties
                they work with.
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                Where you have otherwise provided your consent to us for sharing or transferring your personal information
              </span>
            </li>
          </ol>
        </li>
        <li class="list-item">
          <span class="list-item-title">YOUR PRIVACY RIGHTS.</span>
          <ol class="PrivacyPolicy__sublist">
            <li> <span class="number"></span>
              <span class="text">
                <span class="bold">Rights:</span> You can exercise your rights by contacting us at <a href="mailto: privacy@capsulab.com">privacy@capsulab.com</a>
                Subject to legal and other permissible considerations, we will make a reasonable effort to honor your request promptly in accordance
                with applicable law or inform you if we need further personal information in order to fulfil your request. When processing your request,
                we may ask you for additional personal information to confirm or verify your identity and for security purposes, before processing and/or
                honoring your request. We reserve the right to charge a fee where permitted by law, for instance if your request is manifestly unfounded
                or excessive. In the event that your request would adversely affect the rights and freedoms of others (for example, would impact the duty
                of confidentiality we owe to others) or if we are legally entitled to deal with your request in a different way than requested, we will
                address your request to the maximum extent possible, all in accordance with applicable law.
              </span>
            </li>
            <li> <span class="number"></span>
              <span class="text">
                <span class="bold">Marketing emails – opt-out:</span> You may choose not to receive marketing email of this type by sending
                a single email with the subject "BLOCK" to <a href="mailto: privacy@capsulab.com">unsubscribe@capsulab.com</a>. Please note
                that the email must come from the email account you wish to block OR if you receive an unwanted email from us, you can use
                the unsubscribe link found at the bottom of the email to opt out of receiving future emails, and we will process your
                request within a reasonable time after receipt
              </span>
            </li>
          </ol>
        </li>
        <li class="list-item">
          <span class="bold">USE BY CHILDREN.</span> We do not offer our Website, products or services for use by children and, therefore,
          we do not knowingly collect information from, and/or about children under the age of 18. If you are under the age of 18, do not
          provide any information to us without the involvement of a parent or a guardian. In the event that we become aware that you
          provide information in violation of applicable privacy laws, we reserve the right to delete it. If you believe that we might have
          any such information, please contact us at <a href="mailto: privacy@capsulab.com">privacy@capsulab.com</a>
        </li>
        <li class="list-item">
          <span class="bold">INTERACTION WITH THIRD PARTY PRODUCTS.</span> We enable you to interact with third party websites, products and
          services (such as mobile software applications) that are not owned, or controlled, by us (each, a <span class="bold">“Third Party Service”</span>).
          We are not responsible for the privacy practices or the content of such Third Party Services. Please be aware that Third Party Services
          can collect personal information from you. Accordingly, we encourage you to read the terms and conditions and privacy policies of each
          Third Party Service
        </li>
        <li class="list-item">
          <span class="bold">ANALYTIC TOOLS/COOKIES.</span> You may control the way in which your devices permit the use of cookies and
          other tracking technologies. If you so choose, you may block or delete our cookies from your browser; however, blocking or
          deleting cookies may cause some of the Services, including features and general functionality, to work incorrectly. Some of the
          cookies and tracking technologies that we use in our Services include:
          <ol class="PrivacyPolicy__disc-list">
            <li>
              <span class="bold">Google Analytics.</span> The Website uses a tool called <span class="bold">“Google Analytics”</span> to
              collect information about use of the Website. Google Analytics collects information such as how often users visit this Website,
              what pages they visit when they do so, and what other websites they used prior to coming to this Website. We use the information
              we get from Google Analytics to maintain and improve the Website and our products. We do not combine the information collected
              through the use of Google Analytics with information we collect. Google’s ability to use and share information collected by
              Google Analytics about your visits to this Website is restricted by the Google Analytics Terms of Service, available at
              <a href="https://marketingplatform.google.com/about/analytics/terms/us/" target="_blank">https://marketingplatform.google.com/about/analytics/terms/us/</a>,
              and the Google Privacy Policy, available at <a href="http://www.google.com/policies/privacy/" target="_blank">http://www.google.com/policies/privacy/</a>.
              You may learn more about how Google collects and processes data specifically in connection with Google Analytics at
              <a href="http://www.google.com/policies/privacy/partners/" target="_blank">http://www.google.com/policies/privacy/partners/</a>.
              You may prevent your data from being used by Google Analytics by downloading and installing the Google Analytics Opt-out Browser
              Add-on, available at <a href="https://tools.google.com/dlpage/gaoptout/" target="_blank">https://tools.google.com/dlpage/gaoptout/</a>.
            </li>
            <li>
              Through our Website we may allow third party advertising partners to set technologies and other tracking tools to collect information regarding your activities and your device (e.g., your IP address, mobile identifiers, page(s) visited, location, time of day). We may also combine and share such information and other information (such as demographic information and past purchase history) with third party advertising partners. These advertising partners may use this information (and similar information collected from other websites) for purposes of delivering targeted advertisements to you when you visit third party websites within their networks. This practice is commonly referred to as “interest-based advertising” or “online behavioral advertising”. We may allow access to other data collected by the services to share information that may be useful, relevant, valuable or otherwise of interest to you.
            </li>
            <li>
              We also use some targeting and retargeting tools, such as Google.
            </li>
            <li>
              We reserve the right to remove or add new analytic tools, cookies, pixels and other tracking technologies
            </li>
          </ol>
        </li>
        <li class="list-item">
          <span class="list-item-title">SPECIFIC PROVISIONS APPLICABLE UNDER CALIFORNIA PRIVACY LAW</span>
          <ol class="PrivacyPolicy__sublist">
            <li> <span class="number"></span>
              <span class="text">
                <span class="bold underline">California Privacy Rights:</span> California Civil Code Section 1798.83 permits our customers
                who are California residents to request certain information regarding our disclosure of information to third parties for
                their direct marketing purposes. To make such a request, please send an email to <a href="mailto: privacy@capsulab.com">privacy@capsulab.com</a>. Please note that
                we are only required to respond to one request per customer each year
              </span>
            </li>
            <li>
              <span class="number"></span>
              <span class="text">
                <span class="bold underline">Our California Do Not Track Notice:</span> Do Not Track (<span class="bold">“DNT”</span>) is
                a privacy preference that users can set in certain web browsers.  Please note that we do not respond to or honor DNT
                signals or similar mechanisms transmitted by web browsers, but we may allow third parties, such as companies that provide
                us with analytics tools, to collect personally identifiable information about an individual consumer’s online activities
                over time and across different web sites when a consumer uses the Services
              </span>
            </li>
          </ol>
        </li>
        <li class="list-item">
          <span class="bold">CONTACT US</span>. If you have any questions, concerns or complaints regarding our compliance with this notice
          and the data protection laws, or if you wish to exercise your rights, we encourage you to first contact us at
          <a href="mailto: privacy@capsulab.com">privacy@capsulab.com</a>
        </li>
      </ol>
    </div>
  </div>
</template>
<script>
export default {
  name: 'PrivacyPolicy'
}
</script>

<style lang="scss">
.PrivacyPolicy {
  @include content;
  color: black;
  margin: 60px 3%;
  &__title {
    text-align: center;
    font-size: 28px;
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: 30px;
  }
  &__subtitle {
    font-size: 22px;
    text-align: center;
    font-style: italic;
    margin-bottom: 10px;
  }
  &__text {
    font-size: 18px;
    line-height: 150%;
    margin-top: 20px;
  }
  &__list {
    counter-reset: list1;
    &>li {
      list-style-type: decimal;
      padding-left: 15px;
      margin-left: 15px;
      counter-increment: list1;
    }
    .PrivacyPolicy__sublist {
      counter-reset: list2;
      & > li {
        display: flex;
        &>.number {
          margin-right: 10px;
          display: block;
          &:before {
            counter-increment: list2;
            content: counter(list1) "." counter(list2) ". ";
          }
        }
      }
      &-sub {
        counter-reset: list3;
        & > li {
          display: flex;
          &>.number {
            margin-right: 10px;
            display: block;
            &:before {
              counter-increment: list3;
              content: counter(list1) "." counter(list2) "." counter(list3) ". ";
            }
          }
        }
      }
    }
  }
  &__letter-list {
    list-style: lower-alpha;
    margin-left: 20px;

    &>li {
      margin-left: 15px;
      padding-left: 15px;
    }
  }
  &__disc-list {
    list-style: disc;
    margin-left: 18px;
    &>li {
      margin: 9px 15px 9px 0;
      padding-left: 15px;
    }
  }
  @media screen and (max-width: 810px) {
    padding: 3%;
    margin: calc(20px + #{$header-width}) 0 200px 0
  }
}
.underline {
  text-decoration: underline;
  pre {
    margin: 10px 0;
    font-weight: 600;
  }
}
.bold {
  font-weight: 600;
}
.list-item {
  margin: 10px 0;
  &-title {
    font-weight: 600;
    display: block;
    margin-bottom: 10px;
  }
}
</style>
